import { useState, useEffect } from 'react'
import { supabase } from '../utils/supabase'
import './AuthStyles.css'
import abraLogo from '../assets/abra-logo.png'

export default function AuthForm() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle'|'loading'|'success'|'error'>('idle')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const t = setTimeout(() => setStatus('idle'), 5000)
      return () => clearTimeout(t)
    }
  }, [status])

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setStatus('loading')

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin + '/dashboard' },
      })
      
      if (error) throw error
      
      setStatus('success')
      setEmail('')
    } catch (error) {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="auth-page">
      {/* Added extra glow element for enhanced background effect */}
      <div className="extra-glow"></div>
      
      <div className="auth-modal">
        <div className="auth-logo">
          <div className="logo-icon">
            <img src={abraLogo} alt="Abra logo" />
          </div>
        </div>

        <h1 className="auth-title">Sign in to <span className="highlight">Abra Console</span></h1>
        <p className="auth-subtitle">Welcome! Please sign in to continue</p>

        {status === 'success' && (
          <div className="auth-alert success">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Check your email for your magic link.</span>
          </div>
        )}
        
        {status === 'error' && (
          <div className="auth-alert error">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>Something went wrong. Please try again.</span>
          </div>
        )}

        <div className="oauth-buttons">
          <button
            type="button"
            className="oauth-button"
            onClick={() => supabase.auth.signInWithOAuth({ 
                provider: 'github',
                options: {
                    redirectTo: 'https://console.abra-actions.com/dashboard',
                  },
            })}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            GitHub
          </button>
          
          <button
            type="button"
            className="oauth-button"
            onClick={() => supabase.auth.signInWithOAuth({ 
                provider: 'google',
                options: {
                    redirectTo: 'https://console.abra-actions.com/dashboard',
                  }, 
            })}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032 s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2 C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
            </svg>
            Google
          </button>
        </div>

        <div className="auth-separator">or</div>

        <form onSubmit={handleAuth} className="auth-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email address</label>
            <input
              id="email"
              type="email"
              className="auth-input"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="button-loading">
                <span className="spinner"></span>
                <span>Signing In...</span>
              </div>
            ) : (
              <>Continue</>
            )}
          </button>
        </form>

        <div className="auth-footer-wrapper">
          <div className="secured-by">
            <span>Secured by Abra</span>
          </div>
        </div>
      </div>
    </div>
  )
}